'use client'
import React from 'react';
import { Star } from 'lucide-react';
import { motion } from 'motion/react';
import { TCompaniesConfig } from '@/app/config';

const SuccessStories = ({config}:{config:TCompaniesConfig["SuccessStories"]}) => {
 

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <motion.div
        key={index}
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ 
          delay: index * 0.1, 
          type: "spring", 
          stiffness: 200,
          damping: 10
        }}
      >
        <Star
          className={`w-4 h-4 ${
            index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
          }`}
        />
      </motion.div>
    ));
  };

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-8">
        {/* Animated Header */}
        <motion.div 
          className="text-center mb-16"
          variants={ {
            hidden: { opacity: 0, y: -30 },
            visible: { 
              opacity: 1, 
              y: 0,
              transition: {
                type: "spring",
                stiffness: 100,
                damping: 10,
                duration: 0.8
              }
            }
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
          >
           {config.title}
          </motion.h2>
          <motion.p 
            className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
          >
           {config.description}
          </motion.p>
        </motion.div>

        {/* Animated Success Stories Grid */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {config.successStories.map((story, index) => (
            <motion.div 
              key={index} 
              className={`${story.bgColor} rounded-2xl p-6 border border-green-100 cursor-pointer`}
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 50,
                  scale: 0.9
                },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  scale: 1,
                  transition: {
                    type: "spring",
                    stiffness: 100,
                    damping: 12,
                    duration: 0.6
                  }
                }
              }}
              whileHover={{ 
                scale: 1.03,
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                transition: { type: "spring", stiffness: 300, damping: 20 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Animated Rating Stars */}
              <div className="flex items-center space-x-1 mb-4">
                {renderStars(story.rating)}
              </div>

              {/* Company Info with Animation */}
              <motion.div 
                className="flex flex-row justify-between w-full mb-6"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className='flex flex-col'>
                  <motion.h3 
                    className="text-xl font-bold text-gray-900"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.4, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    {story.company}
                  </motion.h3>
                  <motion.p 
                    className="text-sm text-gray-600"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    {story.industry}
                  </motion.p>
                </div>
                
                <motion.img 
                  src={story.imageUrl} 
                  alt="logo" 
                  className='w-16 h-16'
                  initial={{ opacity: 0, rotate: -90, scale: 0 }}
                  whileInView={{ opacity: 1, rotate: 0, scale: 1 }}
                  transition={{ 
                    delay: index * 0.1 + 0.6, 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 15 
                  }}
                  viewport={{ once: true }}
                  whileHover={{ rotate: 5, scale: 1.1 }}
                />
              </motion.div>

              {/* Animated Main Metric */}
              <motion.div 
                className="mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.7, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <motion.h4 
                  className="text-lg font-semibold text-green-600 mb-2"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {story.mainMetric}
                </motion.h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {story.description}
                </p>
              </motion.div>
              
              {/* Animated Metrics Pills */}
              <div className="flex flex-wrap gap-2">
                {story.metrics.map((metric, metricIndex) => (
                  <motion.div
                    key={metricIndex}
                    className="bg-green-100 text-green-700 text-xs px-3 py-2 rounded-full font-medium"
                    custom={metricIndex}
                    variants={{
                      hidden: { opacity: 0, scale: 0.8, x: -20 },
                      visible: (index: number) => ({
                        opacity: 1,
                        scale: 1,
                        x: 0,
                        transition: {
                          delay: index * 0.1,
                          type: "spring",
                          stiffness: 150,
                          damping: 10
                        }
                      })
                    }}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    whileHover={{ 
                      scale: 1.1, 
                      backgroundColor: "rgb(187 247 208)",
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                  >
                    <span className="text-green-600">{metric.value}</span>{' '}
                    <span className="text-gray-600">{metric.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SuccessStories;